import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Activity, Trophy, AlertTriangle, Download, Loader2 } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid, RadialBarChart, RadialBar } from "recharts";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { ASSESSMENT_MODULES, tierForScore } from "../data/assessments";
import { useAssessments } from "../context/AssessmentContext";
import { exportGrowthReadinessPDF } from "../lib/pdfExport";

const GrowthReadiness = () => {
  const { results } = useAssessments();

  const data = useMemo(() => {
    return ASSESSMENT_MODULES.map(m => ({
      slug: m.slug,
      name: m.name,
      short: m.name.split(" ")[0],
      pct: results[m.slug]?.score?.pct ?? 0,
      completed: !!results[m.slug],
    }));
  }, [results]);

  const completed = data.filter(d => d.completed);
  const overall = completed.length ? Math.round(completed.reduce((a, b) => a + b.pct, 0) / completed.length) : 0;
  const tier = tierForScore(overall);

  const gaugeData = [{ name: "score", value: overall, fill: tier.color }];

  const dashboardRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const downloadPDF = async () => {
    if (exporting) return;
    if (completed.length === 0) { toast.error("Complete at least one assessment to download a report."); return; }
    setExporting(true);
    try {
      await exportGrowthReadinessPDF({ data, overall, tier, dashboardEl: dashboardRef.current });
      toast.success("Scorecard downloaded.");
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="growth-readiness-page">
      <PageHeader
        eyebrow="Business Growth Readiness Index"
        title={<>Your enterprise <em className="text-brand-green font-serif italic">scorecard.</em></>}
        subtitle="A consolidated dashboard combining every assessment you've completed — into a single, executive-grade view."
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Download bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">
              {completed.length} of {ASSESSMENT_MODULES.length} modules · Updated {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </div>
            <button
              onClick={downloadPDF}
              disabled={exporting || completed.length === 0}
              data-testid="growth-download-pdf"
              className="inline-flex items-center gap-2 bg-brand-green text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-brand-green-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting ? "Generating…" : "Download Scorecard PDF"}
            </button>
          </div>

          {/* Coverage banner if incomplete */}
          {completed.length < ASSESSMENT_MODULES.length && (
            <div data-testid="coverage-banner" className="mb-12 bg-brand-gold/10 border border-brand-gold/30 rounded-sm p-6 flex items-start gap-4">
              <AlertTriangle className="h-5 w-5 text-brand-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-brand-ink">Partial coverage · {completed.length} of {ASSESSMENT_MODULES.length} modules completed</div>
                <div className="text-sm text-brand-slate mt-1">Complete remaining modules to unlock a full Enterprise Readiness Score with industry benchmarks.</div>
              </div>
              <Link to="/assessment" data-testid="coverage-cta" className="inline-flex items-center gap-2 bg-brand-ink text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-brand-green transition-colors whitespace-nowrap">
                Continue Assessment <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}

          {/* KPI Row */}
          <div ref={dashboardRef} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div data-testid="overall-gauge" className="lg:col-span-4 bg-brand-ink text-white rounded-sm p-8 relative overflow-hidden">
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-green/40 blur-3xl" />
              <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-brand-gold/20 blur-3xl" />
              <div className="relative">
                <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold">Enterprise Readiness</div>
                <div className="mt-4 h-48 -mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart innerRadius="70%" outerRadius="100%" data={gaugeData} startAngle={180} endAngle={-180}>
                      <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                      <RadialBar dataKey="value" cornerRadius={4} background={{ fill: "rgba(255,255,255,0.08)" }} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center -mt-32 mb-32 relative z-10">
                  <div className="font-serif text-6xl tracking-tight">{overall}<span className="text-3xl text-white/40">%</span></div>
                  <div className="mt-1 text-sm" style={{ color: tier.color === "#0F4C3A" ? "#C5A265" : tier.color }}>{tier.tier}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 bg-white border border-black/5 rounded-sm p-8">
              <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">Maturity Position</div>
              <div className="mt-5 space-y-3">
                {[
                  { label: "Leader", min: 85, color: "#0F4C3A" },
                  { label: "Advanced", min: 70, color: "#1A6B53" },
                  { label: "Developing", min: 55, color: "#C5A265" },
                  { label: "Emerging", min: 40, color: "#B07A3A" },
                  { label: "At Risk", min: 0, color: "#B33A3A" },
                ].map(b => (
                  <div key={b.label} className={`flex items-center gap-3 px-3 py-2 rounded-sm ${tier.tier === b.label ? "bg-brand-green-50 border-l-4 border-brand-green" : ""}`}>
                    <div className="h-2 w-2 rounded-full" style={{ background: b.color }} />
                    <div className="flex-1 text-sm text-brand-ink">{b.label}</div>
                    <div className="text-xs text-brand-slate">{b.min}+%</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 bg-white border border-black/5 rounded-sm p-8">
              <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-brand-slate">
                <Trophy className="h-4 w-4 text-brand-gold" /> Strategic Recommendation
              </div>
              <div className="font-serif text-2xl text-brand-ink mt-4 leading-tight">{tier.desc}</div>
              <Link to="/contact" data-testid="growth-contact-cta" className="inline-flex items-center gap-2 mt-6 text-brand-green font-medium text-sm hover:gap-3 transition-all">
                Book a strategy review <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Radar + Modules Bar */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 bg-white border border-black/5 rounded-sm p-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">10-Axis Composite</div>
                  <h3 className="font-serif text-2xl text-brand-ink mt-1">Enterprise capability radar</h3>
                </div>
                <Activity className="h-5 w-5 text-brand-green" />
              </div>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={data.map(d => ({ name: d.short, you: d.pct, benchmark: 70 }))} outerRadius="80%">
                    <PolarGrid stroke="#E2E8F0" />
                    <PolarAngleAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                    <Radar name="Benchmark" dataKey="benchmark" stroke="#C5A265" fill="#C5A265" fillOpacity={0.08} strokeDasharray="4 4" />
                    <Radar name="You" dataKey="you" stroke="#0F4C3A" fill="#0F4C3A" fillOpacity={0.4} strokeWidth={2} />
                    <Tooltip contentStyle={{ background: "#1A1C19", border: "none", borderRadius: 2, color: "white", fontSize: 12 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white border border-black/5 rounded-sm p-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">Module Breakdown</div>
                  <h3 className="font-serif text-2xl text-brand-ink mt-1">Ranked scores</h3>
                </div>
                <BarChart3 className="h-5 w-5 text-brand-green" />
              </div>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...data].sort((a, b) => b.pct - a.pct)} layout="vertical" margin={{ left: 0 }}>
                    <CartesianGrid horizontal={false} stroke="#F1F5F9" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 11 }} />
                    <YAxis type="category" dataKey="short" tick={{ fill: "#64748B", fontSize: 11 }} width={100} />
                    <Tooltip cursor={{ fill: "rgba(15,76,58,0.05)" }} contentStyle={{ background: "#1A1C19", border: "none", borderRadius: 2, color: "white", fontSize: 12 }} />
                    <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                      {data.map((d, i) => (
                        <Cell key={i} fill={d.pct === 0 ? "#E2E8F0" : d.pct >= 85 ? "#0F4C3A" : d.pct >= 70 ? "#1A6B53" : d.pct >= 55 ? "#C5A265" : "#B33A3A"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Module Grid */}
          <div className="mt-12">
            <h3 className="font-serif text-2xl text-brand-ink mb-6">Module status</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {data.map(d => (
                <Link
                  key={d.slug}
                  to={d.completed ? `/assessment/${d.slug}/results` : `/assessment/${d.slug}`}
                  data-testid={`module-status-${d.slug}`}
                  className="bg-white border border-black/5 p-5 rounded-sm hover:shadow-card-hover transition-all"
                >
                  <div className="text-xs tracking-[0.2em] uppercase text-brand-slate">{d.short}</div>
                  <div className="font-serif text-3xl mt-2" style={{ color: d.completed ? tierForScore(d.pct).color : "#E2E8F0" }}>
                    {d.completed ? `${d.pct}%` : "—"}
                  </div>
                  <div className="mt-2 text-xs text-brand-slate">{d.completed ? tierForScore(d.pct).tier : "Not started"}</div>
                  <div className="mt-3 h-1 bg-black/5 rounded overflow-hidden">
                    <div className="h-full" style={{ width: `${d.pct}%`, background: d.completed ? tierForScore(d.pct).color : "transparent" }} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GrowthReadiness;